/**
 * AI Scanner hashtag-selection telemetry (#731).
 *
 * Thin, fire-and-forget wrappers over the existing New Relic `postMetric`
 * instrumentation (`src/utils/userMetrics.js`). This module is the single
 * source of truth for the hashtag telemetry event names and attribute keys —
 * register these as metric definitions in GitLab per team convention
 * (see specs/i731/contracts/newrelic-events.md).
 *
 * All three events reuse the existing send path (no new pipeline) and carry
 * only `postMetric`'s default context — no member/user identifier (FR-012).
 */
import { postMetric } from './userMetrics';

/** Shared metric category, PascalCase to match existing metricTypes. */
const METRIC_TYPE = 'AiScanner';

/** Event names (the New Relic `eventType`) for hashtag telemetry. */
export const AI_SCANNER_HASHTAG_EVENTS = Object.freeze({
  TOGGLE: 'AiScannerHashtagToggle',
  COMMIT: 'AiScannerHashtagCommit',
  CLEAR: 'AiScannerHashtagClear'
});

/**
 * Dispatch a telemetry event without ever letting a failure reach the caller.
 * Telemetry is fire-and-forget: neither a synchronous throw nor an async
 * rejection may block a hashtag toggle or the next-step navigation
 * (#731 FR-007). Send-failure logging is already handled inside the New Relic
 * send path (`userMetrics` -> `eventLogger`), so this intentionally swallows
 * here without additional `console.*`/`Logger` reporting.
 */
const dispatch = (metricName, customData) => {
  try {
    Promise.resolve(
      postMetric({ metricType: METRIC_TYPE, metricName, customData })
    ).catch(() => {
      // Intentional no-op: telemetry must never break the hashtag UI (FR-007).
    });
  } catch {
    // Intentional no-op: a synchronous failure must not break the UI (FR-007).
  }
};

/**
 * One event per hashtag select/unselect toggle (FR-001, FR-002).
 * @param {{ hashtagId: string, action: 'select'|'unselect', selectedCount: number }} data
 * @returns {void} Fire-and-forget; never throws.
 */
export const trackHashtagToggle = ({ hashtagId, action, selectedCount }) => {
  dispatch(AI_SCANNER_HASHTAG_EVENTS.TOGGLE, {
    hashtagId,
    action,
    selectedCount
  });
};

/**
 * One event when the member advances via 「NNN 件を表示」 (FR-003, FR-004).
 * `hashtagIds` is emitted as a comma-separated string, not an array: New Relic's
 * Event API stores only scalar attribute values and silently drops array-valued
 * ones, so sending an array leaves no queryable `hashtagIds` column at all.
 * @param {{ hashtagIds: string[], selectedCount: number, resultCount: number }} data
 * @returns {void} Fire-and-forget; never throws.
 */
export const trackHashtagCommit = ({
  hashtagIds,
  selectedCount,
  resultCount
}) => {
  dispatch(AI_SCANNER_HASHTAG_EVENTS.COMMIT, {
    hashtagIds: hashtagIds.join(','),
    selectedCount,
    resultCount
  });
};

/**
 * One event when the member taps "clear all" (FR-011). Emits a single clear
 * event rather than a per-chip `unselect` burst.
 * @param {{ hashtagIds: string[] }} data
 * @returns {void} Fire-and-forget; never throws.
 */
export const trackHashtagClear = ({ hashtagIds }) => {
  dispatch(AI_SCANNER_HASHTAG_EVENTS.CLEAR, {
    // Comma-separated string, not an array — see trackHashtagCommit.
    hashtagIds: hashtagIds.join(','),
    selectedCount: 0
  });
};
